import type { MailSignature } from "../../services/mailSignatureService";
import type { MailboxAccess } from "../../services/graphMailService";

/**
 * Reine Anzeige-Logik der Signatur-Verwaltung.
 *
 * Bewusst OHNE die Präzedenzregel — die lebt einmalig in
 * `signatureResolve.ts` und wird sowohl vom Verfassen-Fenster als auch von
 * dieser Section genutzt. Zwei Kopien derselben Regel würden zwangsläufig
 * auseinanderlaufen.
 */

const norm = (v: string | null | undefined): string => (v ?? "").trim().toLowerCase();

/**
 * Zeigt die Signatur auf ein Postfach, auf das der Benutzer keinen
 * Sendezugriff (mehr) hat?
 *
 * Wird nur markiert, nicht automatisch bereinigt: Nutzerarbeit wird nie still
 * gelöscht, und bei erneuter Zuweisung funktioniert die Signatur wieder.
 */
export function isOrphanMailbox(
    row: Pick<MailSignature, "mailboxAddress">,
    mailboxes: Array<Pick<MailboxAccess, "mailboxAddress">>,
): boolean {
    const mb = norm(row.mailboxAddress);
    if (!mb) return false; // allgemeiner Standard kann nicht verwaisen
    return !mailboxes.some((m) => norm(m.mailboxAddress) === mb);
}

/** Anzeigename eines Postfachs: „Name <adresse>" bzw. nur die Adresse. */
export function mailboxLabel(m: Pick<MailboxAccess, "mailboxAddress" | "displayName">): string {
    return m.displayName ? `${m.displayName} <${m.mailboxAddress}>` : m.mailboxAddress;
}

/**
 * Text für das Standard-Kennzeichen in der Liste.
 * `null`, wenn die Signatur kein Standard ist.
 */
export function describeSignatureScope(
    row: Pick<MailSignature, "isDefault" | "mailboxAddress">,
    mailboxes: Array<Pick<MailboxAccess, "mailboxAddress" | "displayName">>,
): string | null {
    if (!row.isDefault) return null;
    const mb = norm(row.mailboxAddress);
    if (!mb) return "Standard · alle Postfächer";
    const known = mailboxes.find((m) => norm(m.mailboxAddress) === mb);
    return `Standard · ${known?.displayName || row.mailboxAddress}`;
}

/** Beschriftung des Standard-Schalters, abhängig vom gewählten Postfach. */
export function describeDefaultToggle(mailboxAddress: string): string {
    const mb = norm(mailboxAddress);
    return mb
        ? `Als Standard für ${mailboxAddress}`
        : "Als allgemeiner Standard (greift, wenn für das Postfach keiner gesetzt ist)";
}

/** Grobe Binärgröße aller eingebetteten `data:`-Bilder im HTML. */
export function embeddedImageBytes(html: string): number {
    let total = 0;
    const re = /src\s*=\s*(["'])\s*data:[^;,]*;base64,([^"']*)\1/gi;
    for (let m = re.exec(html); m !== null; m = re.exec(html)) {
        const payload = (m[2] ?? "").replace(/\s+/g, "");
        if (!payload) continue;
        const pad = payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0;
        total += Math.max(0, Math.floor((payload.length / 4) * 3) - pad);
    }
    return total;
}
